import { computed, readonly, ref, watch } from "vue";

export type UiTheme = "light" | "dark";

const THEME_STORAGE_KEY = "etax.ui.theme";
const theme = ref<UiTheme>("light");
let initialized = false;

function readSavedTheme(): UiTheme {
  if (typeof window === "undefined") return "light";
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark"
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

function applyTheme(value: UiTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = value;
  document.documentElement.style.colorScheme = value;
}

export function initializeTheme(): void {
  if (initialized) return;
  initialized = true;
  theme.value = readSavedTheme();
  applyTheme(theme.value);
  watch(theme, (value) => {
    applyTheme(value);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch {
      // Theme remains usable when storage is unavailable.
    }
  });
}

export function useTheme() {
  initializeTheme();
  const isDark = computed(() => theme.value === "dark");

  function setTheme(value: UiTheme): void {
    theme.value = value;
  }

  function toggleTheme(): void {
    theme.value = isDark.value ? "light" : "dark";
  }

  return {
    theme: readonly(theme),
    isDark,
    dark: isDark,
    setTheme,
    toggleTheme,
    toggle: toggleTheme,
    initializeTheme,
  };
}
